import React, { useState, useMemo, useEffect } from "react";
import TopNavbar from "../../components/TopbarNav";
import Sidebar from "../../components/MasterSideBar";
import PayrollTable from "../../components/payroll/payroll-table";
import PayslipTable from "../../components/payroll/PaySlipTable";
import Button from '../../components/Button';
import PayrollModal from "../../components/PayrollModal";
import Pagination from "../../components/Pagination";
import { fetchPayrolls, fetchPayslips, fetchPayrollStats } from "../../api/payroll-api";
import { Users, DollarSign, Clock } from 'lucide-react'; // Added icons
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

// Replaced PayrollCard with this StatCard component as per request
const StatCard = ({ title, value, icon }) => {
  const formattedValue = typeof value === 'number'
    ? value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })
    : value;

  return (
    <div className="bg-orange-50 p-4 rounded-xl shadow-md flex flex-col justify-between items-start w-full">
      <div className="flex items-center gap-2 mb-1 text-sm text-gray-600">
        {icon}
        <span>{title}</span>
      </div>
      <p className="text-lg sm:text-2xl font-medium text-gray-800">{formattedValue}</p>
    </div>
  );
};


const Payroll = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [payrollData, setPayrollData] = useState([]);
  const [payslipData, setPayslipData] = useState([]);
  const [stats, setStats] = useState(null); // New state for payroll stats
  const [loading, setLoading] = useState(true);

  const itemsPerPage = 5;

  const refreshData = async () => {
    try {
      setLoading(true);
      const [payrollsResponse, payslipsResponse, statsResponse] = await Promise.all([
        fetchPayrolls(),
        fetchPayslips(),
        fetchPayrollStats(),
      ]);

      const payrolls = Array.isArray(payrollsResponse.data) ? payrollsResponse.data : [];
      const payslips = Array.isArray(payslipsResponse.data) ? payslipsResponse.data : [];

      setPayrollData(payrolls);
      setPayslipData(payslips);
      setStats(statsResponse.data);
    } catch (error) {
      console.error("Error fetching payroll data:", error);
      setPayrollData([]);
      setPayslipData([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  // Pagination logic for Payroll
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(() => payrollData.slice(indexOfFirstItem, indexOfLastItem), [payrollData, currentPage, indexOfFirstItem, indexOfLastItem]);
  const totalPages = Math.ceil(payrollData.length / itemsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  // Pagination logic for Payslips
  const [payslipPage, setPayslipPage] = useState(1);
  const payslipsPerPage = 5;
  const indexOfLastPayslip = payslipPage * payslipsPerPage;
  const indexOfFirstPayslip = indexOfLastPayslip - payslipsPerPage;
  const currentPayslips = useMemo(() => payslipData.slice(indexOfFirstPayslip, indexOfLastPayslip), [payslipData, payslipPage, indexOfFirstPayslip, indexOfLastPayslip]);
  const totalPayslipPages = Math.ceil(payslipData.length / payslipsPerPage);

  const handlePayslipPrevious = () => {
    if (payslipPage > 1) setPayslipPage(payslipPage - 1);
  };
  const handlePayslipNext = () => {
    if (payslipPage < totalPayslipPages) setPayslipPage(payslipPage + 1);
  };

  // Helper function to format payroll data for export
  const mapPayrollToExportRow = (entry) => ({
    "No": entry._id,
    "Name": entry.staff ? `${entry.staff.firstName} ${entry.staff.lastName}` : 'N/A',
    "Department": entry.staff?.department || 'N/A',
    "Month": entry.month || 'N/A',
    "Year": entry.payPeriod?.year || 'N/A',
    "Start Date": entry.payPeriod?.startDate ? new Date(entry.payPeriod.startDate).toLocaleDateString() : 'N/A',
    "End Date": entry.payPeriod?.endDate ? new Date(entry.payPeriod.endDate).toLocaleDateString() : 'N/A',
    "Basic Salary": entry.salary?.basic?.toFixed(2) || '0.00',
    "Allowances": entry.totalAllowances?.toFixed(2) || '0.00',
    "Bonuses": entry.totalBonuses?.toFixed(2) || '0.00',
    "Arrears": entry.arrears?.toFixed(2) || '0.00',
    "Deductions": entry.totalDeductions?.toFixed(2) || '0.00',
    "Gross Pay": entry.grossPay?.toFixed(2) || '0.00',
    "Net Pay": entry.netPay?.toFixed(2) || '0.00',
    "Payment Method": entry.paymentMethod?.replace(/_/g, ' ') || 'N/A',
    "Approval Status": entry.approval?.status?.replace(/_/g, ' ') || 'N/A',
  });

  // Helper function to format payslip data for export
  const mapPayslipToExportRow = (entry) => ({
    "Name": entry.staff ? `${entry.staff.firstName} ${entry.staff.lastName}` : 'N/A',
    "Department": entry.staff?.department || 'N/A',
    "Month": entry.month || 'N/A',
    "Gross Pay": entry.grossPay?.toFixed(2) || '0.00',
    "Payment Status": entry.paymentStatus?.replace(/_/g, ' ') || 'N/A',
  });

  // Payroll Export Functions
  const handlePayrollExport = (format) => {
    if (!payrollData.length) {
      alert("No payroll data to export!");
      return;
    }
    const exportData = payrollData.map(mapPayrollToExportRow);
    exportToFormat(exportData, format, "payroll_data");
  };

  // Payslip Export Functions
  const handlePayslipExport = (format) => {
    if (!payslipData.length) {
      alert("No payslip data to export!");
      return;
    }
    const exportData = payslipData.map(mapPayslipToExportRow);
    exportToFormat(exportData, format, "payslip_data");
  };

  const exportToFormat = (dataToExport, format, filename) => {
    switch (format) {
      case "excel":
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, filename);
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const excelBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(excelBlob, `${filename}.xlsx`);
        break;

      case "csv":
        const csvWorksheet = XLSX.utils.json_to_sheet(dataToExport);
        const csv = XLSX.utils.sheet_to_csv(csvWorksheet);
        const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(csvBlob, `${filename}.csv`);
        break;

      case "pdf":
        const doc = new jsPDF("l", "pt", "a4");
        const columns = Object.keys(dataToExport[0]);
        const rows = dataToExport.map(row => columns.map(col => row[col]));
        doc.setFontSize(18);
        doc.setTextColor(237, 125, 49);
        doc.text(`${filename} Export`, 40, 40);
        autoTable(doc, {
          startY: 60,
          head: [columns],
          body: rows,
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [237, 125, 49] },
          theme: "striped",
          margin: { left: 20, right: 20 },
        });
        doc.save(`${filename}.pdf`);
        break;
      default:
        alert("Unsupported export format");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:block">
        <Sidebar onLogout={handleLogout} />
      </div>
      <div className="flex-1 md:ml-64 bg-gray-100 min-h-screen">
        <TopNavbar />
        <main className="pt-20 px-3 sm:px-4 md:px-6 overflow-auto">
          <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6 gap-3 sm:gap-4">
            <h2 className="text-2xl font-bold text-gray-800">Payroll</h2>
            {/* <Button
              onClick={() => setShowModal(true)}
              className="bg-orange-500 text-white text-sm md:text-base font-semibold px-4 py-2 rounded-lg hover:bg-orange-600 shadow-md"
            >
              Add Staff To Payroll
            </Button> */}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {stats && (
              <>
                <StatCard
                  title="Total Gross Pay"
                  value={stats.totalGrossPay}
                  icon={<DollarSign className="text-orange-600 text-2xl" />}
                />
                <StatCard
                  title="Total Deductions"
                  value={stats.totalDeductions}
                  icon={<DollarSign className="text-red-600 text-2xl" />}
                />
                <StatCard
                  title="Pending Payments"
                  value={stats.totalPendingPayments}
                  icon={<Clock className="text-yellow-600 text-2xl" />}
                />
              </>
            )}
          </div>

          {/* Payslip table */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading payslip data...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <PayslipTable
                  data={currentPayslips}
                  onExport={handlePayslipExport}
                />
              </div>
              <Pagination
                onPrevious={handlePayslipPrevious}
                onNext={handlePayslipNext}
                currentPage={payslipPage}
                totalPages={totalPayslipPages}
              />
            </>
          )}


          <PayrollModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              refreshData();
            }}
          />
        </main>
      </div>
    </div>
  );
};

export default Payroll;